import { useMemo } from 'react'

import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import {
  CatalogPermissionSecondaryCategoryConfig,
  type CatalogPermissionSecondaryCategoryConfigConstructor,
} from '@webapp/modules/purchasing/entities/CatalogPermissionSecondaryCategoryConfig'
import {
  DEFAULT_CATALOG_CONFIG_PAGING,
  useCatalogPermissionConfigListState,
} from '@webapp/modules/purchasing/hooks/useCatalogPermissionConfigListState'
import { useListCatalogPermissionSecondaryCategoryConfigsModel } from '../../models/useListCatalogPermissionSecondaryCategoryConfigsModel'

export interface IUseCatalogPermissionSecondaryCategoryConfigListPresenterParams {
  selectedSecondaryCategoryId: Nullable<number>
  onSelectSecondaryCategory: (
    secondaryCategory: CatalogPermissionSecondaryCategoryConfig
  ) => void
}

export const useCatalogPermissionSecondaryCategoryConfigListPresenter = ({
  selectedSecondaryCategoryId,
  onSelectSecondaryCategory,
}: IUseCatalogPermissionSecondaryCategoryConfigListPresenterParams) => {
  const {
    modelParams,
    searchInput,
    handleSearchChange,
    handleSearchSubmit,
    handlePageChange,
  } = useCatalogPermissionConfigListState()

  const { data, isLoading, error } =
    useListCatalogPermissionSecondaryCategoryConfigsModel({
      search: modelParams.search || undefined,
      page: modelParams.page,
      limit: modelParams.limit,
      offset: modelParams.offset,
    })

  const items: CatalogPermissionSecondaryCategoryConfig[] = useMemo(
    () =>
      (data?.data ?? []).map(
        (constructor: CatalogPermissionSecondaryCategoryConfigConstructor) =>
          new CatalogPermissionSecondaryCategoryConfig(constructor)
      ),
    [data?.data]
  )

  const paging: RepoPaginationResponse =
    data?.pagination ?? DEFAULT_CATALOG_CONFIG_PAGING
  const isError = error !== null

  return {
    data: {
      items,
      paging,
    },
    states: {
      isLoading,
      isError,
      searchValue: searchInput,
      selectedSecondaryCategoryId,
    },
    commands: {
      handleSelectSecondaryCategory: onSelectSecondaryCategory,
      handleSearchChange,
      handleSearchSubmit,
      handlePageChange,
    },
  }
}
